import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#preloadVariantGalleries();
    this.#syncBlankState();

    // Initialize View More button functionality
    this.#initViewMoreButton();
  }

  #controller = new AbortController();

  /** @type {Record<string, { key: string, urls: string[] }> | null} */
  #variantGalleries = null;

  /** @type {Set<string> | null} */
  #imageKeys = null;

  /** @type {string | null} */
  #modelSizingInfo = null;

  /** URLs already preloaded across all instances. */
  static #preloadedUrls = new Set();

  /**
   * The option-value key (e.g. "Blue~~~XL") we are waiting to show.
   * Static so it survives element replacement during combined-listing morphs.
   * @type {string | null}
   */
  static #pendingKey = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  static #fallbackTimer;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Called after the Section Rendering API morphs this element in place. Combined-listing color
   * changes use morph (no variant:update event), so reconcile blank/reveal state here.
   */
  updatedCallback() {
    super.updatedCallback();

    this.#variantGalleries = null;
    this.#imageKeys = null;
    this.#modelSizingInfo = null;

    this.#syncBlankState();
    this.#syncModelSizingInfo();
  }

  /**
   * Blanks the media area while waiting for a variant-specific gallery, reveals once it arrives.
   */
  #syncBlankState = () => {
    const pending = MediaGallery.#pendingKey;

    if (!pending) {
      this.classList.remove('is-loading-variant');
      return;
    }

    const showsPendingGallery = this.dataset.variantGallery === 'true' && this.dataset.variantKey === pending;

    if (showsPendingGallery) {
      MediaGallery.#pendingKey = null;
      clearTimeout(MediaGallery.#fallbackTimer);
      this.classList.remove('is-loading-variant');
      return;
    }

    this.classList.add('is-loading-variant');

    clearTimeout(MediaGallery.#fallbackTimer);
    MediaGallery.#fallbackTimer = setTimeout(() => {
      MediaGallery.#pendingKey = null;
      document
        .querySelectorAll('media-gallery.is-loading-variant')
        .forEach((gallery) => gallery.classList.remove('is-loading-variant'));
    }, 2500);
  };

  /**
   * @returns {Record<string, { key: string, urls: string[] }>}
   */
  #getVariantGalleries() {
    if (this.#variantGalleries) return this.#variantGalleries;

    const script = this.querySelector('script[data-variant-galleries]');

    try {
      this.#variantGalleries = script?.textContent ? JSON.parse(script.textContent) : {};
    } catch {
      this.#variantGalleries = {};
    }

    return this.#variantGalleries ?? {};
  }

  /**
   * @returns {Set<string>}
   */
  #getImageKeys() {
    if (this.#imageKeys) return this.#imageKeys;

    this.#imageKeys = new Set(Object.values(this.#getVariantGalleries()).map((entry) => entry.key));

    return this.#imageKeys;
  }

  /**
   * @returns {string}
   */
  #getModelSizingInfo() {
    if (this.#modelSizingInfo != null) return this.#modelSizingInfo;

    const script = this.querySelector('script[data-model-sizing-info]');

    try {
      this.#modelSizingInfo = script?.textContent ? JSON.parse(script.textContent) : '';
    } catch {
      this.#modelSizingInfo = '';
    }

    return this.#modelSizingInfo ?? '';
  }

  /**
   * @param {string} infoHtml
   */
  #applyModelSizingInfo(infoHtml) {
    const badges = this.querySelectorAll('.product-media__model-info-badge');
    const hasInfo = Boolean(infoHtml && infoHtml.trim());

    badges.forEach((badge) => {
      if (!(badge instanceof HTMLElement)) return;

      badge.hidden = !hasInfo;

      if (hasInfo) {
        badge.innerHTML = infoHtml;
      }
    });
  }

  #syncModelSizingInfo() {
    this.#applyModelSizingInfo(this.#getModelSizingInfo());
  }

  /**
   * Builds a selection key from the variant picker (option values joined in option order).
   * @returns {string | null}
   */
  #readCurrentSelectionKey() {
    const scope = this.closest('.shopify-section, dialog');
    const picker = scope?.querySelector('variant-picker');
    if (!picker) return null;

    const values = [];

    for (const option of picker.querySelectorAll('.variant-option')) {
      const checkedRadio = option.querySelector('input[type="radio"]:checked');
      if (checkedRadio instanceof HTMLInputElement) {
        values.push(checkedRadio.value.trim());
        continue;
      }

      const select = option.querySelector('select');
      if (select instanceof HTMLSelectElement) {
        values.push(select.value.trim());
      }
    }

    return values.length ? values.join('~~~') : null;
  }

  #preloadVariantGalleries() {
    const schedule = typeof window.requestIdleCallback === 'function'
      ? (cb) => window.requestIdleCallback(cb, { timeout: 4000 })
      : (cb) => setTimeout(cb, 2500);

    schedule(() => {
      const currentKey = this.dataset.variantKey;
      const width = window.matchMedia('(max-width: 749px)').matches ? 750 : 1200;

      for (const { key, urls } of Object.values(this.#getVariantGalleries())) {
        if (key === currentKey) continue;
        const firstUrl = urls?.[0];
        if (!firstUrl) continue;

        const base = String(firstUrl).split('?')[0];
        const sizedUrl = `${base}?width=${width}`;
        if (MediaGallery.#preloadedUrls.has(sizedUrl)) continue;
        MediaGallery.#preloadedUrls.add(sizedUrl);

        const image = new Image();
        image.fetchPriority = 'low';
        image.decoding = 'async';
        image.src = sizedUrl;
      }
    });
  }

  /**
   * Fires immediately on click, before the section fetch. Blank the gallery if the target
   * variant has its own images so default images never flash while loading.
   */
  #handleVariantSelected = () => {
    const imageKeys = this.#getImageKeys();
    if (imageKeys.size === 0) return;

    const targetKey = this.#readCurrentSelectionKey();
    const targetHasImages = targetKey != null && imageKeys.has(targetKey);
    const alreadyShown = this.dataset.variantGallery === 'true' && this.dataset.variantKey === targetKey;

    MediaGallery.#pendingKey = targetHasImages && !alreadyShown ? targetKey : null;

    this.#syncBlankState();
  };

  /**
   * Makes the visible main image the only high-priority request on a gallery
   * that is about to be inserted, so secondary images cannot jump the queue.
   * @param {HTMLElement} gallery
   */
  #prioritizeMainImage(gallery) {
    if (!(gallery instanceof HTMLElement)) return;

    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    gallery.querySelectorAll('img').forEach((img) => {
      img.setAttribute('loading', 'lazy');
      img.setAttribute('fetchpriority', 'low');
      img.fetchPriority = 'low';
    });

    const mainImage = isMobile
      ? gallery.querySelector('slideshow-slides slideshow-slide img, slideshow-component img.product-media__image')
      : gallery.querySelector('.media-gallery__grid img.product-media__image, .media-gallery__grid img');

    if (!(mainImage instanceof HTMLImageElement)) return;

    mainImage.setAttribute('loading', 'eager');
    mainImage.setAttribute('fetchpriority', 'high');
    mainImage.fetchPriority = 'high';
  }

  #initViewMoreButton = () => {
    const viewMoreButton = this.querySelector('#viewMoreButton');
    const hiddenGallery = this.querySelector('#hiddenMediaGallery');

    if (viewMoreButton && hiddenGallery instanceof HTMLElement) {
      viewMoreButton.addEventListener('click', () => {
        hiddenGallery.style.display = 'grid';

        setTimeout(() => {
          hiddenGallery.classList.add('show');
        }, 10);

        const buttonWrapper = viewMoreButton.closest('.media-gallery__view-more-button-wrapper');
        if (buttonWrapper instanceof HTMLElement) {
          buttonWrapper.style.display = 'none';
        }

        setTimeout(() => {
          const scrollTarget = hiddenGallery.offsetTop;
          window.scrollTo({
            top: scrollTarget,
            behavior: 'smooth',
          });
        }, 100);
      }, { signal: this.#controller.signal });
    }
  };

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!(newMediaGallery instanceof HTMLElement)) return;

    this.#applyModelSizingInfo(this.#readModelSizingInfoFrom(newMediaGallery));

    this.#prioritizeMainImage(newMediaGallery);

    const newIsVariantGallery = newMediaGallery.dataset.variantGallery === 'true';
    const currentIsVariantGallery = this.dataset.variantGallery === 'true';
    const pending = MediaGallery.#pendingKey;

    // While waiting for a specific variant gallery, ignore intermediate renders.
    if (pending) {
      const newMatchesPending = newIsVariantGallery && newMediaGallery.dataset.variantKey === pending;
      if (!newMatchesPending) return;

      this.replaceWith(newMediaGallery);
      return;
    }

    // Both galleries are default — skip unnecessary swap.
    if (!newIsVariantGallery && !currentIsVariantGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * @param {HTMLElement} gallery
   * @returns {string}
   */
  #readModelSizingInfoFrom(gallery) {
    const script = gallery.querySelector('script[data-model-sizing-info]');

    try {
      return script?.textContent ? JSON.parse(script.textContent) : '';
    } catch {
      return '';
    }
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }

  /**
   * Blanks the gallery until a variant-specific render for this selection key arrives.
   * Used by combined-listing color switches (color-variants.js).
   * @param {string | null} key
   */
  static setPendingGallery(key) {
    MediaGallery.#pendingKey = key || null;

    if (!key) {
      clearTimeout(MediaGallery.#fallbackTimer);
      document
        .querySelectorAll('media-gallery.is-loading-variant')
        .forEach((gallery) => gallery.classList.remove('is-loading-variant'));
      return;
    }

    const gallery = document.querySelector('media-gallery');
    gallery?.classList.add('is-loading-variant');

    clearTimeout(MediaGallery.#fallbackTimer);
    MediaGallery.#fallbackTimer = setTimeout(() => {
      MediaGallery.#pendingKey = null;
      document
        .querySelectorAll('media-gallery.is-loading-variant')
        .forEach((el) => el.classList.remove('is-loading-variant'));
    }, 2500);
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}

/** @param {string | null} key */
window.__setMediaGalleryPending = (key) => MediaGallery.setPendingGallery(key);
